/**
 * AusschreibungenTab - the Kunde-Detail «Ausschreibungen» tab (P2 Placement Slice 4, Ch 7.3).
 *
 * A KPI row (offen / in Vorbereitung / beim Kunden / erledigt), a status FilterChip bar + a search
 * box, a Liste|Pipeline SegmentedControl, and the list/pipeline itself. «+ Neues Placement»
 * (gated on canWrite) opens NeuesPlacementDialog, which navigates to the new Placement on success.
 *
 * The server owns the data (list + KPIs); the tab only filters/searches client-side and renders.
 *
 * [Source: apps/web/src/features/entity-detail/components/risikoprofil/RisikoprofilTab.tsx] (tab idiom)
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { CheckCircle2, ClipboardList, FileCheck2, Plus, Send } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { FilterChip } from '../../components/ui/FilterChip';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { EntityLink } from '../../components/ui/EntityLink';
import { DataTable } from '../../components/data/DataTable';
import { KpiCard } from '../entity-detail/components/KpiCard';
import { dateShortDe } from '../../lib/format';
import {
  PLACEMENT_ANLASS_LABELS,
  PLACEMENT_STATUS_STEP_COUNT,
  placementStatusStep,
} from '@brokly/shared';
import type { PlacementListItem } from '@brokly/shared';
import { usePlacementsForKunde } from './usePlacements';
import { PlacementStatusBadge } from './PlacementStatusBadge';
import { PlacementClosureBadge } from './PlacementClosureBadge';
import { PlacementPipeline } from './PlacementPipeline';
import { NeuesPlacementDialog } from './NeuesPlacementDialog';
import type { PlacementPolicyOption } from './NeuesPlacementDialog';

// ── Shared list building blocks (this tab + the firm-wide AusschreibungenListScreen) ─────
// The chips + the filter funnel moved to @brokly/shared (#14A) so the api's count SQL and the web
// share one source. Imported for local use + re-exported for the existing importers;
// PlacementStatusFilter aliases the shared PlacementListView.
import { PLACEMENT_STATUS_FILTER_CHIPS, matchesPlacementFilter } from '@brokly/shared';
import type { PlacementListView } from '@brokly/shared';
export { PLACEMENT_STATUS_FILTER_CHIPS, matchesPlacementFilter };
export type PlacementStatusFilter = PlacementListView;

export function PlacementFortschritt({ item }: { item: PlacementListItem }) {
  const step = placementStatusStep(item.status);
  if (step < 0) return <span className="text-xs text-muted-foreground">-</span>;
  const pct = Math.round(((step + 1) / PLACEMENT_STATUS_STEP_COUNT) * 100);
  return (
    <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted" aria-label={`Fortschritt ${pct}%`}>
      <div className="h-full rounded-full bg-foreground" style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * The Placement list columns (Nr. · [Kunde] · Sparte · Anlass · Fortschritt · Status · Referenzpolice ·
 * Beginn · Abschluss · Betreuer). `kundeCell` inserts the customer column right after Nr. — the
 * firm-wide screen needs it, the per-customer tab does not.
 */
export function placementListColumns<T extends PlacementListItem>(
  openDetail: (id: string) => void,
  options: { kundeCell?: (item: T) => ReactNode } = {},
): ColumnDef<T>[] {
  const kunde: ColumnDef<T>[] = options.kundeCell
    ? [{ id: 'kunde', header: 'Kunde', cell: ({ row }) => options.kundeCell!(row.original) }]
    : [];
  return [
    {
      accessorKey: 'placement_number',
      header: 'Nr.',
      cell: ({ row }) => (
        <EntityLink onActivate={() => openDetail(row.original.id)} className="mono-num text-xs font-medium">
          {row.original.placement_number}
        </EntityLink>
      ),
    },
    ...kunde,
    {
      accessorKey: 'branchen_label',
      header: 'Sparte',
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.branchen_label}</span>,
    },
    {
      accessorKey: 'anlass',
      header: 'Anlass',
      cell: ({ row }) => <span>{PLACEMENT_ANLASS_LABELS[row.original.anlass]}</span>,
    },
    {
      id: 'fortschritt',
      header: 'Fortschritt',
      cell: ({ row }) => <PlacementFortschritt item={row.original} />,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <PlacementStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'policy_number',
      header: 'Referenzpolice',
      cell: ({ row }) => (
        <span className="mono-num text-xs text-muted-foreground">{row.original.policy_number ?? '-'}</span>
      ),
    },
    {
      accessorKey: 'gewuenschter_beginn',
      header: 'Beginn',
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.gewuenschter_beginn ? dateShortDe(row.original.gewuenschter_beginn) : '-'}
        </span>
      ),
    },
    {
      id: 'abschluss',
      header: 'Abschluss',
      cell: ({ row }) => (
        <PlacementClosureBadge
          status={row.original.closure_status}
          startZeitpunkt={row.original.closure_start_zeitpunkt}
          endZeitpunkt={row.original.closure_end_zeitpunkt}
        />
      ),
    },
    {
      accessorKey: 'betreuer_name',
      header: 'Betreuer',
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.betreuer_name ?? '-'}</span>,
    },
  ];
}

export function AusschreibungenTab({
  subjectType,
  subjectId,
  policen,
  canWrite,
}: {
  subjectType: 'person' | 'firma';
  subjectId: string;
  policen: PlacementPolicyOption[];
  canWrite: boolean;
}) {
  const navigate = useNavigate();
  const query = usePlacementsForKunde(subjectId, subjectType);
  const [filter, setFilter] = useState<PlacementStatusFilter>('alle');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'liste' | 'pipeline'>('liste');
  const [createOpen, setCreateOpen] = useState(false);

  const openDetail = (id: string) =>
    void navigate({ to: '/ausschreibungen/$id', params: { id } });

  if (query.isLoading) {
    return <p className="text-sm text-muted-foreground">Lade Ausschreibungen…</p>;
  }
  if (query.isError || !query.data) {
    return (
      <p role="alert" className="text-sm text-status-danger" data-testid="aut-error">
        Die Ausschreibungen konnten nicht geladen werden.
      </p>
    );
  }

  const { items, kpis } = query.data;
  const needle = search.trim().toLowerCase();
  const filtered = items.filter(
    (i) =>
      matchesPlacementFilter(i, filter) &&
      (needle === '' ||
        i.placement_number.toLowerCase().includes(needle) ||
        (i.branchen_label ?? '').toLowerCase().includes(needle)),
  );

  const columns: ColumnDef<PlacementListItem>[] = placementListColumns<PlacementListItem>(openDetail);

  return (
    <div className="flex flex-col gap-5" data-testid="ausschreibungen-tab">
      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard icon={Send} label="Offen" value={String(kpis.offen)} />
        <KpiCard icon={ClipboardList} label="In Vorbereitung" value={String(kpis.in_vorbereitung)} />
        <KpiCard icon={FileCheck2} label="Beim Kunden" value={String(kpis.beim_kunden)} />
        <KpiCard icon={CheckCircle2} label="Erledigt" value={String(kpis.erledigt)} />
      </div>

      {/* Toolbar: filter chips + search + view toggle + create */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1" role="group" aria-label="Nach Status filtern">
          {PLACEMENT_STATUS_FILTER_CHIPS.map((c) => (
            <FilterChip
              key={c.value}
              label={c.label}
              active={filter === c.value}
              onClick={() => setFilter(c.value)}
            />
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Nr. oder Sparte suchen"
          aria-label="Ausschreibungen durchsuchen"
          className="h-8 w-48 rounded-md border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <div className="ml-auto flex items-center gap-2">
          <SegmentedControl
            ariaLabel="Ansicht"
            value={view}
            onValueChange={(v) => setView(v as 'liste' | 'pipeline')}
            options={[
              { value: 'liste', label: 'Liste' },
              { value: 'pipeline', label: 'Pipeline' },
            ]}
          />
          {canWrite && (
            <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="aut-create">
              <Plus className="size-3.5" />
              Neues Placement
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      {filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">
          {items.length === 0 ? 'Noch keine Ausschreibungen.' : 'Keine Treffer für den Filter.'}
        </div>
      ) : view === 'liste' ? (
        <DataTable columns={columns} data={filtered} />
      ) : (
        <PlacementPipeline items={filtered} onOpen={openDetail} />
      )}

      <NeuesPlacementDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        subjectType={subjectType}
        subjectId={subjectId}
        policen={policen}
        onCreated={openDetail}
      />
    </div>
  );
}
